/** Raw audio custody precedes transcription. Each chunk snapshot commits before it is advertised as kept. */
import { CAPTURE_STORE, RAW_CHUNKS_STORE, RAW_RECORDING_STORE, openCaptureDatabase, parseCapture, type PendingCapture } from './captureStorage';
export interface RawRecording {
  id: string;
  ownerId: string;
  purpose: string;
  createdAt: string;
  blob: Blob;
  durationMs: number;
  transcript?: string;
}
interface Chunk { recordingId: string; ownerId: string; index: number; blob: Blob }
function parse(value: unknown): RawRecording {
  const r = value as Partial<RawRecording> | null;
  if (!r || typeof r.id !== 'string' || !r.id || typeof r.ownerId !== 'string' || !r.ownerId || typeof r.purpose !== 'string' || !r.purpose ||
      typeof r.createdAt !== 'string' || !Number.isFinite(Date.parse(r.createdAt)) || !(r.blob instanceof Blob) || !/^audio\/(webm|mp4)(;|$)/.test(r.blob.type) || typeof r.durationMs !== 'number' || !Number.isFinite(r.durationMs) || r.durationMs < 0 || (r.transcript !== undefined && (typeof r.transcript !== 'string' || !r.transcript))) {
    throw new Error('Raw recording custody could not be read. Existing audio remains quarantined and untouched.');
  }
  return r as RawRecording;
}
async function transact(mode: IDBTransactionMode, action: (context: { store: IDBObjectStore; records: RawRecording[]; chunks: IDBObjectStore; parts: Chunk[]; metadata: RawRecording[]; captures?: IDBObjectStore }) => void, transfersCapture = false): Promise<RawRecording[]> {
  let db: IDBDatabase;
  try { db = await openCaptureDatabase(); }
  catch { throw new Error('Raw recording storage could not be opened. Existing audio remains untouched.'); }
  try {
    return await new Promise<RawRecording[]>((resolve, reject) => {
      let reason = 'Raw recording storage could not complete this operation. Its existing audio remains untouched.';
      let tx: IDBTransaction;
      try { tx = db.transaction(transfersCapture ? [RAW_RECORDING_STORE, RAW_CHUNKS_STORE, CAPTURE_STORE] : [RAW_RECORDING_STORE, RAW_CHUNKS_STORE], mode, { durability: 'strict' }); }
      catch { reject(new Error(reason)); return; }
      let records: RawRecording[] = [];
      tx.oncomplete = () => resolve(records);
      tx.onabort = () => reject(new Error(reason));
      try {
      const store = tx.objectStore(RAW_RECORDING_STORE);
      const chunks = tx.objectStore(RAW_CHUNKS_STORE);
      const request = store.getAll();
      request.onsuccess = () => {
        try {
        const parts = chunks.getAll();
        parts.onsuccess = () => {
          try {
            const metadata = request.result.map(parse);
            const audio = parts.result as Chunk[];
            if (!audio.every(p => typeof p.recordingId === 'string' && typeof p.ownerId === 'string' && Number.isSafeInteger(p.index) && p.index >= 0 && p.blob instanceof Blob && p.blob.size > 0 && metadata.some(r => r.id === p.recordingId && r.ownerId === p.ownerId))) {throw new Error('Raw recording chunks could not be read. Existing audio remains quarantined.');}
            records = metadata.map(r => {
              const own = audio.filter(p => p.recordingId === r.id).sort((a, b) => a.index - b.index);
              if (own.length && (r.blob.size || own.some((p, i) => p.index !== i))) {throw new Error('Raw recording chunks are incomplete. Existing audio remains quarantined.');}
              return own.length ? { ...r, blob: new Blob(own.map(p => p.blob), { type: r.blob.type }) } : r;
            });
            action({ store, records, chunks, parts: audio, metadata, captures: transfersCapture ? tx.objectStore(CAPTURE_STORE) : undefined });
          }
          catch (error) { reason = error instanceof Error && error.message.startsWith('Raw recording') ? error.message : reason; tx.abort(); }
        };
        } catch { tx.abort(); }
      };
      } catch { tx.abort(); }
    });
  } finally { db.close(); }
}
export async function keepRawRecording(record: RawRecording): Promise<void> {
  const valid = parse(record);
  await transact('readwrite', ({ store, records, chunks, parts }) => {
    const existing = records.find(r => r.id === valid.id);
    if (existing && (existing.ownerId !== valid.ownerId || existing.purpose !== valid.purpose || existing.createdAt !== valid.createdAt || existing.blob.size > valid.blob.size || (existing.transcript !== undefined && valid.transcript !== undefined && existing.transcript !== valid.transcript))) {
      throw new Error('Raw recording identity conflicts with its original custody. Nothing was replaced.');
    }
    store.put(existing?.transcript !== undefined ? { ...valid, transcript: existing.transcript } : valid);
    for (const part of parts.filter(p => p.recordingId === valid.id)) chunks.delete([part.recordingId, part.index]);
  });
}
export async function listRawRecordings(ownerId: string, purpose?: string): Promise<RawRecording[]> {
  const all = await transact('readonly', () => {});
  return all.filter(r => r.ownerId === ownerId && (purpose === undefined || r.purpose === purpose) && r.blob.size > 0);
}
export async function removeRawRecording(id: string, ownerId: string): Promise<void> {
  await transact('readwrite', ({ store, records, chunks, parts }) => {
    const found = records.find(r => r.id === id);
    if (!found || found.ownerId !== ownerId) {throw new Error('Raw recording does not belong to this account. Its audio remains untouched.');}
    store.delete(id);
    for (const part of parts.filter(p => p.recordingId === id)) chunks.delete([part.recordingId, part.index]);
  });
}

/** Append immutable chunks; a long dump does not rewrite all earlier bytes at every microphone event. */
export async function appendRawRecordingChunk(id: string, ownerId: string, index: number, blob: Blob, durationMs: number): Promise<void> {
  if (!Number.isSafeInteger(index) || index < 0 || !blob.size || !Number.isFinite(durationMs) || durationMs < 0) throw new Error('Raw recording chunk could not be validated.');
  await transact('readwrite', ({ store, chunks, parts, metadata }) => {
    const record = metadata.find(r => r.id === id && r.ownerId === ownerId);
    if (!record || record.blob.size || record.transcript !== undefined || index !== parts.filter(p => p.recordingId === id).length) throw new Error('Raw recording chunk conflicts with its original custody. Existing audio remains untouched.');
    chunks.add({ recordingId: id, ownerId, index, blob });
    store.put({ ...record, durationMs });
  });
}

/** Fresh authority for an existing recording; stale pages must never resurrect a handed-over identity. */
export async function requireRawRecording(id: string, ownerId: string): Promise<RawRecording> {
  const records = await transact('readonly', () => {});
  const record = records.find(r => r.id === id && r.ownerId === ownerId);
  if (!record) throw new Error('Raw recording custody no longer exists for this account. Its old identity cannot be recreated by this page.');
  return record;
}

/** One physical commit transfers custody to the existing queue; a crash cannot leave a replayable raw source after sending. */
export async function transferRawRecordingToCapture(id: string, ownerId: string, text: string): Promise<PendingCapture> {
  let capture: PendingCapture | undefined;
  await transact('readwrite', ({ store, records, chunks, parts, captures }) => {
    if (!captures) throw new Error('Raw recording transfer did not acquire capture custody.');
    const raw = records.find(record => record.id === id && record.ownerId === ownerId && record.purpose === 'capture');
    if (!raw) throw new Error('Raw recording does not belong to this capture account. Its audio remains untouched.');
    capture = parseCapture({ id: raw.id, userId: raw.ownerId, source: 'voice', text, createdAt: raw.createdAt,
      recording: { blob: raw.blob, durationMs: raw.durationMs }, attempts: 0, lastError: null, confirmed: false });
    captures.add(capture);
    store.delete(id);
    for (const part of parts.filter(p => p.recordingId === id)) chunks.delete([part.recordingId, part.index]);
  }, true);
  if (!capture) throw new Error('Raw recording custody did not transfer to a capture.');
  return capture;
}
